import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { supabase } from "../lib/supabase";
import { useOrg } from "../auth/OrgProvider";
import { Card, CardHeader } from "../ui/Card";
import type { Property } from "@lawnops/shared";

export function PropertiesPage() {
  const { t } = useTranslation("properties");
  const { activeOrgId } = useOrg();

  const { data, isLoading, error } = useQuery({
    queryKey: ["properties", activeOrgId],
    enabled: Boolean(supabase && activeOrgId),
    queryFn: async (): Promise<Property[]> => {
      if (!supabase || !activeOrgId) return [];
      const { data, error } = await supabase
        .from("properties")
        .select("*")
        .eq("organization_id", activeOrgId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as Property[];
    },
  });

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">{t("title")}</h1>
        <p className="text-sm text-slate-500">{t("subtitle")}</p>
      </div>
      <Card>
        <CardHeader title={t("listTitle")} />
        {!supabase ? (
          <EmptyState message={t("supabaseEmpty")} />
        ) : isLoading ? (
          <EmptyState message={t("loading")} />
        ) : error ? (
          <EmptyState message={`${t("errorPrefix")}${(error as Error).message}`} />
        ) : data && data.length > 0 ? (
          <ul className="divide-y divide-slate-100">
            {data.map((p) => (
              <li key={p.id} className="py-3">
                <p className="text-sm font-medium text-slate-900">
                  {p.address_line1}
                  {p.address_line2 ? `, ${p.address_line2}` : ""}
                </p>
                <p className="text-xs text-slate-500">
                  {p.city}, {p.state} {p.postal_code}
                  {p.lawn_area_sqft ? ` · ${t("lawnSummary", { count: p.lawn_area_sqft })}` : ""}
                </p>
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState message={t("empty")} />
        )}
      </Card>
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return <p className="py-6 text-center text-sm text-slate-500">{message}</p>;
}
